import { Button, ButtonProps } from '@/components/ui/Button';
import { bookmarkUpdateActionData } from '@/lib/helpers/bookmark';
import { ShowNotificationToastOptions, useNotificationToast } from '@/lib/hooks/useNotificationToast';
import { navigate } from 'gatsby';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import updateLastAction from '../../app/redux/actions/updateLastAction';
import { AppDispatch } from '../../app/redux/configureStore';
import { addBookmark } from '../../app/redux/features/bookmark/addBookmark';
import { BookmarkInfo } from '../../app/redux/features/bookmark/bookmarkSlice';
import { getBookmarks } from '../../app/redux/features/bookmark/getBookmarks';
import { removeBookmark } from '../../app/redux/features/bookmark/removeBookmark';
import { useAppDispatch, useAppSelector } from '../../app/redux/hooks';
import { ActionType } from '../../app/services/authTypes';
import { asyncTrackingEvent } from '../../app/utils/tracking';
import { BookmarkAction, BookmarkActionPostState, BookMarkState } from '../../types/bookmark';

/**
 * If user:
 * - signed in: fires off request to remove or add bookmark.
 * - not signed in: sets the last action to be bookmark related.
 */
const bookmarkItem = debounce(
  (
    bookmarkInfo: BookmarkInfo,
    isSignedIn: boolean,
    dispatch: AppDispatch,
    showNotificationToast: ({ type, title, message, content, config }: ShowNotificationToastOptions) => void,
    bookmarkBtnId: string,
    showToast: undefined | boolean,
    customHtml: any,
    itemId: string,
    addOnly: boolean,
    callback: any,
    updateAction: any,
    contentType?: string
  ) => {
    if (isSignedIn) {
      if (bookmarkInfo.bookmarks[itemId as string]) {
        if (!addOnly) {
          dispatch(removeBookmark(itemId)).then((response: any) => {
            if (!response) {
              showNotificationToast({
                type: 'error',
                message: "Unable to remove a bookmark for an event that's been pre-registered"
              });
            } else if (response.error) {
              console.error(response.error);
              showNotificationToast({
                type: 'error',
                message: 'Sorry, something went wrong.'
              });
            }
          });
        }
      } else {
        const state = contentType && contentType == 'opportunity' ? { label: BookMarkState.Saved } : undefined;
        dispatch(addBookmark({ entityUuid: itemId, state: state })).then((response: any) => {
          if (showToast === undefined || showToast === true) {
            if (!response.error) {
              if (!isEmpty(customHtml)) {
                showNotificationToast({
                  type: 'success',
                  content: customHtml,
                  config: {
                    autoClose: 3000
                  }
                });
              }
            } else {
              showNotificationToast({
                type: 'error',
                message: 'Sorry, something went wrong.'
              });
            }
          }
          // Currently we want the callback to run only after successfully adding a bookmark.
          if (callback) callback(bookmarkInfo);
        });
      }
    } else {
      updateAction(
        location,
        ActionType.BOOKMARK,
        bookmarkUpdateActionData({
          id: itemId,
          bookmarkAction: {
            id: bookmarkBtnId,
            contentId: itemId,
            postState: BookmarkActionPostState.Post,
            context: {
              notificationToast: {
                content: customHtml,
                duplicateContent: <div className="text-sm">Whoops. Looks like you've already bookmarked this!"</div>
              }
            }
          } as BookmarkAction
        })
      );
      navigate('/login');
    }
  },
  150,
  { leading: true, trailing: false }
);

export interface BookmarkProps extends Omit<Partial<ButtonProps>, 'children'> {
  id: string;
  bookmarkLabel: string;
  bookmarkedLabel: string;
  trackName?: string;
  trackAbacus?: string;
  showToast?: boolean;
  updateAction: Function;
  bookmarkBtnId: string;
  customHtml: any;
  callback?: any;
  contentType?: string;
  children: (bookmarked: boolean) => React.ReactNode;
}

/**
 * @return {JSX}
 */
const Bookmark = React.forwardRef<HTMLButtonElement, BookmarkProps>(
  (
    {
      id,
      showToast,
      updateAction,
      bookmarkBtnId,
      customHtml,
      trackName = 'bookmark',
      trackAbacus = 'bookmark',
      callback,
      contentType,
      bookmarkLabel,
      bookmarkedLabel,
      children,
      size,
      variant,
      appearance,
      icon,
      className
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { showNotificationToast } = useNotificationToast();
    const bookmarkInfo = useAppSelector((state) => state.bookmarkInfo);
    const isSignedIn = useAppSelector((state) => state.auth.isSignedIn);

    useEffect(() => {
      if (isSignedIn) {
        // User is signed in but we have initial state for bookmarks.
        if (bookmarkInfo.status == 'idle') {
          dispatch(getBookmarks());
        }
      }
    }, [isSignedIn, bookmarkInfo.bookmarks]);

    let btnText = bookmarkLabel;
    let allowBookmark = true;
    let disabledBookmark: boolean = false;
    let circleClasses: string = 'bg-transparent';
    // Check if user has item saved.
    if (bookmarkInfo.bookmarks[id]) {
      allowBookmark = false;
      btnText = bookmarkedLabel;
      disabledBookmark = bookmarkInfo.bookmarks[id].state.label === BookMarkState.PreRegistered ? true : false;
      circleClasses = 'bg-primary-50';
    }
    const trackingInfo = {
      track: trackName,
      trackAbacus: trackAbacus,
      trackId: id,
      trackValue: allowBookmark,
      trackText: btnText
    };

    useEffect(() => {
      // Check if user is signed in
      if (isSignedIn) {
        // Check if user has item saved.
        if (bookmarkInfo.bookmarks[id]) {
          allowBookmark = false;
          btnText = bookmarkedLabel;
          disabledBookmark = bookmarkInfo.bookmarks[id].state.label === BookMarkState.PreRegistered ? true : false;
        }
      }
    }, [isSignedIn, bookmarkInfo.bookmarks, id]);

    // Remove or add the item to the users bookmarks on click.
    const onClick = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      bookmarkItem(
        bookmarkInfo,
        isSignedIn,
        dispatch,
        showNotificationToast,
        bookmarkBtnId,
        showToast,
        customHtml,
        id,
        false,
        callback,
        updateAction,
        contentType
      );
    };

    return (
      <Button
        ref={ref}
        onClick={(e) => {
          asyncTrackingEvent(trackingInfo);
          onClick(e);
        }}
        size={size}
        variant={variant}
        appearance={appearance}
        icon={icon}
        className={className}
        disabled={disabledBookmark}
      >
        {children(bookmarkInfo.bookmarks[id] ? true : false)}
      </Button>
    );
  }
);

const mapDispatchToProps = {
  updateAction: updateLastAction
};

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(Bookmark);
